package com.htmlagent.site.theme

import com.varabyte.kobweb.compose.ui.graphics.Color

/**
 * The neutral scale, twelve steps in the Radix model, in both modes.
 *
 * Every step does the same job in both scales: 1-2 app background; 3-5 component background
 * (normal, hover, active); 6-8 separators and borders; 9-10 solid and its hover; 11-12
 * secondary and primary text. The ratios noted on each step are measured against the
 * surface it sits on.
 *
 * The border of a control is step 8, not 7: it is the only one reaching the 3:1 that WCAG
 * demands from the outline of something usable.
 */
object Slate {

    /** Dark mode. It has a blue tint: slate, not grey. */
    object Dark {
        /** App background. */
        val S1 = Color.rgb(0x070D1A)

        /** Subtle background: an area set apart from the canvas without being a card. */
        val S2 = Color.rgb(0x0A1324)

        /** Component background. Primary text 14.77:1, muted 8.29:1. */
        val S3 = Color.rgb(0x101A2E)

        /** Component on hover. */
        val S4 = Color.rgb(0x14213A)

        /** Active or selected component. Primary text 12.34:1, muted 6.92:1. */
        val S5 = Color.rgb(0x192946)

        /** Separator. 1.38:1 is enough: it does not outline anything usable. */
        val S6 = Color.rgb(0x1F3357)

        /** Border of an interactive element that is not a control. 1.69:1. */
        val S7 = Color.rgb(0x28406E)

        /** The border of a control. 3.35:1 over the component background. */
        val S8 = Color.rgb(0x536D9E)

        /** Neutral solid. */
        val S9 = Color.rgb(0x647FB0)

        /** Neutral solid on hover. */
        val S10 = Color.rgb(0x758FBE)

        /**
         * Secondary text. Lc 64.4 in APCA over the lightest composited surface it sits on
         * (the glass pill).
         *
         * Calibrated in APCA and not in WCAG because that is the metric for Radix step 11
         * (Lc 60 minimum): a comfortable WCAG value can fall short here, and dark mode needs
         * more separation than its light counterpart.
         */
        val S11 = Color.rgb(0xB6C2D8)

        /** Primary text. 16.43:1 in WCAG, Lc 90.7 in APCA. */
        val S12 = Color.rgb(0xE7EDF7)
    }

    /** Light mode, with the roles inverted. */
    object Light {
        val S1 = Color.rgb(0xFBFCFE)
        val S2 = Color.rgb(0xF5F7FB)

        /** Primary text 15.25:1, muted 6.60:1. */
        val S3 = Color.rgb(0xEDF0F7)
        val S4 = Color.rgb(0xE5EAF3)

        /** Primary text 13.49:1. */
        val S5 = Color.rgb(0xDCE3EF)
        val S6 = Color.rgb(0xCFD8E8)
        val S7 = Color.rgb(0xB3C0D8)

        /**
         * The border of a control. 3.58:1 over the canvas and 3.22:1 over a component.
         *
         * Darker than its dark-mode counterpart on purpose: over a light background it takes
         * more pigment to separate the same amount.
         */
        val S8 = Color.rgb(0x7286AB)
        val S9 = Color.rgb(0x64748B)
        val S10 = Color.rgb(0x566072)

        /** Secondary text. 7.33:1. */
        val S11 = Color.rgb(0x4A5568)

        /** Primary text. 16.95:1. */
        val S12 = Color.rgb(0x121A2A)
    }
}

/**
 * The accent, in the same twelve-step structure as [Slate].
 *
 * Only the steps an accent needs are declared: tinted surfaces, border, solid with its
 * hover, and the text tone.
 */
object Cobalt {

    object Dark {
        /** Tinted surface: the user bubble, an active segment. */
        val S3 = Color.rgb(0x052650)
        val S4 = Color.rgb(0x073063)
        val S5 = Color.rgb(0x0A3A76)

        /** Tinted border. */
        val S7 = Color.rgb(0x1C4F96)

        /** The solid. 6.55:1 over the app background, 4.90:1 over an active one. */
        val S9 = Color.rgb(0x5095FF)

        /** The solid on hover. */
        val S10 = Color.rgb(0x6BA2FF)

        /** What goes on top of the solid. 6.46:1. */
        val OnSolid = Color.rgb(0x060F1F)

        /** Accent as text. */
        val S11 = Color.rgb(0x7EAEFF)
    }

    object Light {
        val S3 = Color.rgb(0xE2EBFB)
        val S4 = Color.rgb(0xD5E1F8)
        val S5 = Color.rgb(0xC6D7F4)
        val S7 = Color.rgb(0x99B6EA)

        /** The solid. 5.25:1 over the canvas; white on top gives 5.39:1. */
        val S9 = Color.rgb(0x0667D4)
        val S10 = Color.rgb(0x005BBF)

        val OnSolid = Color.rgb(0xFFFFFF)
        val S11 = Color.rgb(0x075ABA)
    }
}

/** Status, in both modes. Color is never the only cue: it always comes with text. */
object Status {
    object Dark {
        val Danger = Color.rgb(0xFF9AA8)
        val Working = Color.rgb(0x8FD6B0)
    }

    object Light {
        val Danger = Color.rgb(0xB3213A)
        val Working = Color.rgb(0x1E7A4E)
    }
}
